/**
 * S4's contribution to S2's storage tree-extension registry (review C C7): menu
 * action descriptors for the Tables folder and table nodes, the persistent
 * `Create table…` row under the Tables folder, and one execution hook that
 * `executeStorageContextMenuAction` consults (the same hook serves a clicked
 * action row and a picked menu item). The base Open / Copy URL / Pin / Refresh
 * items and every preceding slice's items come from S2's composition.
 */
import type { ContextMenuAction } from '../../../../explorer/models/TreeModel';
import type { StorageService } from '../../../../../types/azureStorage';
import type { StorageContextMenuDeps, StorageMenuNode } from '../../../../database-objects/services/storageContextMenuService';
import {
  registerStorageTreeExtension,
  type StorageMenuContext,
  type StorageServiceFolderAction,
} from '../../../../database-objects/services/storageTreeExtensions';
import { getConnectionById } from '../../../../connections/utils/connectionLookup';
import { STORAGE_READ_ONLY_REASON } from '../../../../../core/engines/azureStorageRefusal';
import { openCreateStorageTableDialog, openDeleteStorageTableDialog } from '../components/storageTableDialogStore';
import { tableAdminRefusal, type TableAdminTarget } from '../model/tableAdminRefusals';
import { toast } from '../../../../../components/ui/toaster';

export const S4_CREATE_TABLE_ACTION = 's4-create-table';
export const S4_DELETE_TABLE_ACTION = 's4-delete-table';
const OUT_OF_SCOPE_REASON = 'This attachment cannot administer this table.';

const tableOf = (node: StorageMenuNode): string | undefined =>
  node.metadata.resource.kind === 'table' ? node.metadata.resource.table : undefined;

/** A resource-scoped attachment administers nothing but its own table (and never creates siblings). */
function outOfScope(node: StorageMenuNode): boolean {
  const scope = getConnectionById(node.metadata.connectionId).connection?.azureStorageScope;
  if (scope?.kind !== 'resource') return false;
  return node.type === 'storage-service-folder' || scope.resource.kind !== 'table' || scope.resource.table !== tableOf(node);
}

const admin = (id: string, label: string, icon: string, reason: string | undefined, destructive = false): ContextMenuAction => ({
  id, label, icon, disabled: !!reason, ...(reason ? { disabledReason: reason } : {}), ...(destructive ? { destructive: true } : {}),
});

/**
 * Why a Create / Delete table is unavailable on this node: read-only, then the attachment scope, then (C2 R10) Azure's
 * own known refusal of tables/write or tables/delete — never the entity-read refusal; a delete refusal is the node's
 * own table's (C2-01: roles can be scoped to one table).
 */
function adminReason(node: StorageMenuNode, ctx: Pick<StorageMenuContext, 'readOnly'>, target: TableAdminTarget): string | undefined {
  if (ctx.readOnly) return STORAGE_READ_ONLY_REASON;
  if (outOfScope(node)) return OUT_OF_SCOPE_REASON;
  return tableAdminRefusal(node.metadata.connectionId, target);
}

/** S4 menu factory for S2's registry (`getStorageMenuItems`). */
export function s4MenuItems(node: StorageMenuNode, ctx: StorageMenuContext): ContextMenuAction[] {
  if (node.type === 'storage-action') return [];
  if (node.type === 'storage-service-folder') {
    return node.metadata.service === 'table' ? [admin(S4_CREATE_TABLE_ACTION, 'Create table', 'plus', adminReason(node, ctx, { op: 'create' }))] : [];
  }
  const table = tableOf(node);
  return table ? [admin(S4_DELETE_TABLE_ACTION, 'Delete table', 'trash-2', adminReason(node, ctx, { op: 'delete', table }), true)] : [];
}

/** The persistent `Create table…` row under the Tables folder (S2's `serviceFolderActionRows` seam). */
export const s4ServiceFolderActionRows = (service: StorageService): readonly StorageServiceFolderAction[] =>
  service === 'table' ? [{ id: S4_CREATE_TABLE_ACTION, label: 'Create table…' }] : [];

export function registerS4StorageTreeExtension(): () => void {
  return registerStorageTreeExtension({ id: 's4-tables', slice: 's4', menuItems: s4MenuItems, serviceFolderActionRows: s4ServiceFolderActionRows });
}

/** `true` when the action id is S4's (handled here); `false` leaves it to the next slice / S1's switch. */
export function executeS4StorageTreeAction(actionId: string, node: StorageMenuNode, deps: Pick<StorageContextMenuDeps, 'track'>): boolean {
  if (actionId === S4_CREATE_TABLE_ACTION) {
    // The Tables folder's persistent "Create table…" row has no disabled state: a known refusal is said instead.
    const refusal = tableAdminRefusal(node.metadata.connectionId, { op: 'create' });
    if (refusal) {
      toast.error({ title: 'Create table unavailable', description: refusal });
      return true;
    }
    openCreateStorageTableDialog(node.metadata.connectionId);
    deps.track('feature:storage_table_admin', { action: 'open_create' });
    return true;
  }
  if (actionId === S4_DELETE_TABLE_ACTION) {
    const table = tableOf(node);
    if (table) {
      openDeleteStorageTableDialog(node.metadata.connectionId, table);
      deps.track('feature:storage_table_admin', { action: 'open_delete' });
    }
    return true;
  }
  return false;
}
